import { createContext, useContext, useState } from 'react'
import { useToast } from '../components/ui/toast'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import { AlertDialog } from '../components/ui/alert-dialog'
import { Banner } from '../components/ui/banner'

const NotificationContext = createContext(null)

export function NotificationProvider({ children }) {
  const toast = useToast()
  const [confirmDialog, setConfirmDialog] = useState(null)
  const [alertDialog, setAlertDialog] = useState(null)
  const [banners, setBanners] = useState([])

  const notify = {
    // Toast notifications
    success: (message, options = {}) => {
      return toast.showToast(message, 'success', options)
    },
    
    error: (message, options = {}) => {
      return toast.showToast(message, 'error', options)
    },
    
    warning: (message, options = {}) => {
      return toast.showToast(message, 'warning', options)
    },
    
    info: (message, options = {}) => {
      return toast.showToast(message, 'info', options)
    },

    // Confirmation dialog (returns a Promise)
    confirm: (options) => {
      return new Promise((resolve) => {
        setConfirmDialog({
          ...options,
          open: true,
          onConfirm: () => {
            resolve(true)
            setConfirmDialog(null)
          },
          onCancel: () => {
            resolve(false)
            setConfirmDialog(null)
          },
          onOpenChange: (open) => {
            if (!open) {
              resolve(false)
              setConfirmDialog(null)
            }
          }
        })
      })
    },

    // Alert dialog (returns a Promise)
    alert: (options) => {
      return new Promise((resolve) => {
        setAlertDialog({
          ...options,
          open: true,
          onClose: () => {
            resolve()
            setAlertDialog(null)
          },
          onOpenChange: (open) => {
            if (!open) {
              resolve()
              setAlertDialog(null)
            }
          }
        })
      })
    },

    // Banner notification
    banner: (options) => {
      const id = Date.now() + Math.random()
      setBanners(prev => [...prev, { id, ...options }])
      return id
    },

    // Dismiss a specific banner
    dismissBanner: (id) => {
      setBanners(prev => prev.filter(b => b.id !== id))
    },

    // Dismiss all banners
    dismissAllBanners: () => {
      setBanners([])
    }
  }

  return (
    <NotificationContext.Provider value={notify}>
      {/* Banners at the top */}
      {banners.map(banner => (
        <Banner
          key={banner.id}
          {...banner}
          onDismiss={() => {
            banner.onDismiss?.()
            notify.dismissBanner(banner.id)
          }}
        />
      ))}

      {/* Main content */}
      {children}

      {/* Toast container */}
      <toast.ToastContainer />

      {/* Confirm dialog */}
      {confirmDialog && <ConfirmDialog {...confirmDialog} />}

      {/* Alert dialog */}
      {alertDialog && <AlertDialog {...alertDialog} />}
    </NotificationContext.Provider>
  )
}

export function useNotification() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}
